import { useBoolean, useRequest } from 'ahooks'
import { Optional } from 'utility-types'
import { useEffect, useId } from 'react'

import { asyncSuccessResolver, toast } from '$app/utilities'
import { MFAChallengeCarroIDApprovalRequest } from '$services/api/types'
import { errorMessageResolver, successResolver } from '$services/api/common'
import { subscribeRtcEvent } from '$services/rtc/auth'

import { FactorHookOptions } from './types'

type UseCarroIDApprovalOptions = FactorHookOptions

export const useCarroIDApproval = ({
  preferred_mfa_token_type = 'ott',
  authRTC: rtc,
  apiInstance: { mfaChallengeCarroIDApprovalRequest },
  onVerified
}: UseCarroIDApprovalOptions) => {
  const toastID = useId()
  const [isDenied, { setTrue: deny, setFalse: undeny }] = useBoolean()

  const challengeReq = useRequest(
    asyncSuccessResolver(mfaChallengeCarroIDApprovalRequest, successResolver),
    {
      manual: true,
      onError: deny,
      onBefore: undeny
    }
  )
  const { challenge_id } = { ...challengeReq.data?.data }

  const requests = [challengeReq]

  const isAnyLoading = requests.map(({ loading }) => loading).includes(true)

  const errorMsg = errorMessageResolver(challengeReq.error)

  const challenge = ({
    type = preferred_mfa_token_type,
    ...rest
  }: Optional<MFAChallengeCarroIDApprovalRequest, 'type'>) => {
    const { runAsync } = challengeReq

    toast.promise(runAsync({ type, ...rest }), {
      error: errorMessageResolver,
      loading: 'Verifying...',
      success: 'Approval requested.',
      id: toastID
    })
  }

  const reset = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
    undeny()

    toast.dismiss(toastID)
  }

  const reChallenge = challengeReq.refresh

  useEffect(
    () => {
      if (!rtc || !challenge_id) return

      const subs = [
        subscribeRtcEvent('carroid_denied', {
          rtc,
          validate: ({ data }) => data.challenge_id === challenge_id,
          action: deny
        }),

        subscribeRtcEvent('carroid_approved', {
          rtc,
          validate: ({ data }) => data.challenge_id === challenge_id,
          action: ({ data: { mfa_token } }) => onVerified(mfa_token)
        })
      ]

      return () => {
        subs.forEach(unsub => unsub())
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rtc, challenge_id]
  )

  return {
    challenge,
    isAnyLoading,
    reset,
    reChallenge,
    errorMsg,
    isDenied
  }
}

export default useCarroIDApproval
